<template>
    <div>
        <h1 class="mb-6">{{$t('LOGIN_TITLE')}}</h1>
        <v-alert role="alert" type="error" icon="error" dense :value="!!error" class="mb-4">{{ error }}</v-alert>
        <announcement/>
        <template v-if="clockHasDrifted">
            <v-alert role="alert" type="warning" icon="warning" dense class="mb-4">{{$t('LOGIN_CLOCK_DRIFT_WARNING')}}</v-alert>            
            <a  target='_blank' :href="clockDriftArticleUrl">{{$t('LOGIN_CLOCK_DRIFT_HELP_MESSAGE')}}</a>
        </template>
        <template v-else>
            <v-spacer></v-spacer>
            <v-spacer></v-spacer>
            <login-buttons @confirm="confirm" :providers="['google', 'azure', 'credential_anonymous']" :credential-code="error ? undefined : credentialCode" mode="" />
            <router-link :to="{name: 'registration'}">{{$t('LOGIN_INVITATION_LOGIN_TEXT')}}</router-link>
            <template v-if="featureSignInAsAnonymousUserEnabled">
                <br>
                <a  target='_blank' :href="anonymousUsersArticleUrl">{{$t('LOGIN_ANONYMOUS_USER_HELP_MESSAGE')}}</a>
            </template>
            <br>
            <a target='_blank' :href="loginSupportUrl">{{$t('LOGIN_ERROR_HELP_MESSAGE')}}</a>
        </template>
    </div>
</template>

<script lang="ts">
import Vue from "vue";
import LoginButtons from "./LoginButtons.vue";
import Component from "vue-class-component";
import { useAuthorizationStore } from "@/stores/authorizationStore";
import Config from '../../config';
import Announcement from '@utils/Announcement.vue';
import { resolve } from 'url';
import { CreateUserParams } from '@/utils/userCreation/createUserParams';
import AddUserMethod from '@/interfaces/addUserMethod';
import {Credential} from "@cyber-range/cyber-range-api-credential-client"
import { useErrorStore } from "@/stores/errorStore";
import { useAuthenticationStore } from "@/stores/authenticationStore";
import { useApiClientStore } from "@/stores/apiClientStore";
import { useEntitlementStore } from "@/stores/entitlementStore";
import { useUserStore } from "@/stores/userStore";
import { storeToRefs } from "pinia";

@Component({
    components: { LoginButtons, Announcement }
})
export default class Login extends Vue {
    getSsoToken = () => useAuthenticationStore().getSsoToken();
    loadingBegin = () => useApiClientStore().loadingBegin();
    loadingEnd = () => useApiClientStore().loadingEnd();
    detectSystemClockDrift = () => useAuthenticationStore().detectSystemClockDrift();
    
    get getClaims() { return useEntitlementStore().getClaims; }
    get getDisplayName() { return useAuthenticationStore().displayName; }
    get canViewExerciseArea() { return useAuthorizationStore().canViewExerciseArea; }
    fetchClaims = () => useEntitlementStore().fetchClaims();

    clockHasDrifted:boolean = false;

    // TODO: Change to composition api
    error = storeToRefs(useErrorStore()).errorMessage;
    // END TODO

    get credentialCode() {
        return this.$route.query ? this.$route.query.code : undefined;
    }

    async confirm()
    {
        if (this.credentialCode)
        {
            this.loadingBegin();
            await useUserStore().createUser(new CreateUserParams(AddUserMethod.credential, Credential.idFromDisplayName(this.getDisplayName)));
        }

        // Disable all buttons while the user is being redirected to the destination.
        this.loadingBegin();

        await this.fetchClaims();

        let ssoToken:string;
        
        try
        {
            ssoToken = await this.getSsoToken();
        }
        catch(e)
        {
            useErrorStore().setError({message:this.$t('LOGIN_ACCOUNT_NOT_FOUND')});
            this.loadingEnd();
            return;
        }

        if(this.$route.query.referrer_url && this.getClaims('role').some(claim => claim.value === 'coursewareadmin'))
        {
            await this.redirectToUrl(<string> this.$route.query.referrer_url, ssoToken);
        }
        else if(this.$route.query.redirect_url)
        {
            await this.redirectToUrl(<string> this.$route.query.redirect_url, ssoToken);
        }
        else if(this.canViewExerciseArea)
        { 
            window.location.href = Config.CYBER_RANGE_UI_BASE_URL + `?s=${ssoToken}`;
        }
        else
        {
            window.location.href = Config.HOMEPAGE_UI_BASE_URL + `?s=${ssoToken}`;
        }
    }

    async redirectToUrl(url:string, ssoToken:string): Promise<boolean>
    {
        if (url.startsWith('/'))
        {
            this.$router.push(url);
            return true;
        }

        if(!Config.REDIRECT_URLS.split(',').some(u => u === url || new RegExp(u).test(url) === true))
        {
            useErrorStore().setError({ message: this.$t("INVALID_REDIRECT_URL", { url }) });
            return false;
        }

        if(!url.includes('?'))
        {
            url += '?';
        }    

        window.location.href = `${url}&s=${ssoToken}`;
        return true;
    }

    get loginSupportUrl() {
        return Config.LOGIN_SUPPORT_URL ? Config.LOGIN_SUPPORT_URL : resolve(Config.SUPPORT_URL, '/troubleshooting/cant-login.html');
    }

    get anonymousUsersArticleUrl() {
        return Config.LOGIN_SUPPORT_URL ? Config.LOGIN_SUPPORT_URL : resolve(Config.SUPPORT_URL, '/faq/us-what-is-anonymous-password.html');
    }

    get clockDriftArticleUrl() {
        return Config.LOGIN_SUPPORT_URL ? Config.LOGIN_SUPPORT_URL : resolve(Config.SUPPORT_URL, '/troubleshooting/cant-login.html#pclaptop-system-clock-time-date-skew');
    }

    get featureSignInAsAnonymousUserEnabled():boolean
    {
        return !!Config.FEATURE_SIGN_IN_AS_ASNONYMOUS_USER_ENABLED;
    }

    async mounted()
    {       
        this.clockHasDrifted = await this.detectSystemClockDrift();
    }
}
</script>

<style scoped>
a {
    text-decoration: none !important;
}
</style>